import { useState, useCallback, useEffect } from 'react';
import metrics from '@/services/metrics';
import { getSectionCanvasId } from '@/components/widget/visualizations/internal/utils';
import { useCopyImage } from '@/components/Editor/share/hooks/useCopyImage';
import { downloadRecording, recordCanvas, waitForSelector } from './elementToVideo';
import elementToImage, { downloadImage } from './elementToImage';
import { createImageDownloadError, createImagePreviewError, createVideoDownloadError } from './errors';
import ChartShareModal, { SHARE_MODAL_FOOTER_ID } from './ChartShareModal';
import OffScreenChartPreview from './ChartPreview';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PREFIX = 'components: Editor: share: ChartShare:';
const DEFAULT_SIZE = 'size_1x';
const DEFAULT_FILE_TYPE = 'png';
const mapSizeToScale = {
  size_1x: 1,
  size_2x: 2,
  size_3x: 3
};

const EditorChartShareModal = _ref => {
  let {
    isVisible,
    sectionId,
    widget,
    workbook,
    workbookMeta,
    updatedAt,
    fileName,
    layoutWidth = 1000,
    layoutHeight = 1000,
    previewCanvasRef,
    hidePremiumOptions,
    onClose,
    onComplete,
    onImageCopy,
    onError = () => undefined,
    onSignup
  } = _ref;
  const widgetId = `${widget.id || ''}-share`;
  const {
    0: previewIndex,
    1: setPreviewIndex
  } = useState(-1);
  const {
    0: fileType,
    1: setFileType
  } = useState(DEFAULT_FILE_TYPE);
  const {
    0: size,
    1: setSize
  } = useState(DEFAULT_SIZE);
  const {
    0: shareType,
    1: setShareType
  } = useState(null);
  const {
    0: previewImageUrl,
    1: setPreviewImageUrl
  } = useState(null);
  const {
    0: isSharing,
    1: setIsSharing
  } = useState(false); // Generate the preview image
  // when the modal is visible

  useEffect(() => {
    if (!previewCanvasRef.current || !isVisible) return;
    let active = true;
    elementToImage({
      fileType,
      captureElement: previewCanvasRef.current
    }).then(dataUrl => {
      if (!active) return;
      setPreviewImageUrl(dataUrl);
    }).catch(err => {
      typeof onError === 'function' && onError(createImagePreviewError(`${PREFIX} elementToImage: failed: ${err}`, err));
    });
    return () => {
      active = false;
    };
  }, [fileType, updatedAt, isVisible, previewCanvasRef, onError]); // Reset to default state when modal is closed

  useEffect(() => {
    if (!isVisible) {
      setShareType(null);
    }
  }, [isVisible]);
  const {
    handleCopyImage
  } = useCopyImage({
    captureElement: previewCanvasRef,
    pixelRatio: mapSizeToScale[size],
    onImageCopy,
    onError,
    onLoadingChange: setIsSharing
  });
  const handleDownloadImage = useCallback(() => {
    if (!previewCanvasRef.current) return;
    setIsSharing(true);
    elementToImage({
      fileType,
      captureElement: previewCanvasRef.current,
      pixelRatio: mapSizeToScale[size]
    }).then(dataUrl => {
      typeof onComplete === 'function' && onComplete('image');
      downloadImage(dataUrl, `${fileName}.${fileType}`);
    }).catch(err => {
      onError(createImageDownloadError(`${PREFIX} elementToImage: failed: ${err}`, err));
    }).finally(() => setIsSharing(false));
  }, [fileName, fileType, size, previewCanvasRef, onError, onComplete]);
  const handleChartRecording = useCallback(async () => {
    // Increment the index to force a re-render
    // to ensure that animations are captured
    setPreviewIndex(prev => prev + 1);
    setIsSharing(true); // Find the chart canvas

    let chartCanvas = null;

    try {
      // Wait for the chart canvas to be rendered
      chartCanvas = await waitForSelector(`#${getSectionCanvasId(widgetId, sectionId)}`, 1000);
    } catch (err) {
      onError(createVideoDownloadError(`${PREFIX} waitForSelector: failed: ${err}`, err));
      setIsSharing(false);
      return;
    } // Record the canvas


    let recording;

    try {
      var _widget$sections, _widget$sections$sect, _sectionConfig$animat, _sectionConfig$animat2;

      // Lookups the section config
      const sectionConfig = (widget === null || widget === void 0 ? void 0 : (_widget$sections = widget.sections) === null || _widget$sections === void 0 ? void 0 : (_widget$sections$sect = _widget$sections[sectionId]) === null || _widget$sections$sect === void 0 ? void 0 : _widget$sections$sect.config) || {};
      const delay = ((_sectionConfig$animat = sectionConfig.animation) === null || _sectionConfig$animat === void 0 ? void 0 : _sectionConfig$animat.delay) ?? 0;
      const duration = ((_sectionConfig$animat2 = sectionConfig.animation) === null || _sectionConfig$animat2 === void 0 ? void 0 : _sectionConfig$animat2.duration) ?? 3000;
      recording = await recordCanvas({
        canvas: chartCanvas,
        duration: delay + duration // Add delay to the duration

      });
    } catch (err) {
      onError(createVideoDownloadError(`${PREFIX} recordCanvas: failed: ${err}`, err));
      setIsSharing(false);
      return;
    } // Download the recording


    try {
      downloadRecording(recording, fileName + '.webm');
      typeof onComplete === 'function' && onComplete('video');
    } catch (err) {
      onError(createVideoDownloadError(`${PREFIX} downloadRecording: failed: ${err}`, err));
    } finally {
      setIsSharing(false);
    }
  }, [widget, widgetId, sectionId, fileName, onError, onComplete]);
  const handleShareTypeChange = useCallback(changed => {
    // Track the share type change
    if (changed !== 'free_image_video') {
      metrics.sendEvent(`exp_${changed}`, {}, {
        maxEventsPerSession: 1
      });
    }

    if (['public_view_link', 'notion_embed_link', 'wordpress_embed_link'].includes(changed)) {
      typeof onSignup === 'function' && onSignup(changed);
    }

    setShareType(changed); // Scroll to the footer with a delay
    // to ensure that the footer is visible

    setTimeout(() => {
      var _document$getElementB;

      (_document$getElementB = document.getElementById(SHARE_MODAL_FOOTER_ID)) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  }, [onSignup]);
  const handleClose = useCallback(() => {
    typeof onClose === 'function' && onClose();
    setShareType(null);
  }, [onClose]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(OffScreenChartPreview, {
      widget: widget,
      workbook: workbook,
      workbookMeta: workbookMeta,
      previewIndex: previewIndex,
      updatedAt: updatedAt,
      layoutWidth: layoutWidth,
      layoutHeight: layoutHeight,
      previewCanvasRef: previewCanvasRef
    }), /*#__PURE__*/_jsx(ChartShareModal, {
      sectionId: sectionId,
      widget: widget,
      fileName: fileName,
      fileType: fileType,
      size: size,
      shareType: shareType,
      previewImageUrl: previewImageUrl,
      isVisible: isVisible,
      isSharing: isSharing,
      hidePremiumOptions: hidePremiumOptions,
      onClose: handleClose,
      onCopyImage: handleCopyImage,
      onDownloadImage: handleDownloadImage,
      onDownloadVideo: handleChartRecording,
      onFileTypeChange: setFileType,
      onSizeChange: setSize,
      onShareTypeChange: handleShareTypeChange
    })]
  });
};

export default EditorChartShareModal;